import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type PopupModalMessageTagProps = {
  message: string;
  alternateMessageComp?: ReactNode;
  popupTitle: string;
  popupSubtext: string | ReactNode;
  popupPosition?: "top" | "bottom";
  onHover?: boolean;
};

const POPUP_WIDTH = 400;
const POPUP_MARGIN = 8;
const HOVER_CLOSE_DELAY_MS = 100;
const FADE_OUT_TRANSITION = "opacity 150ms ease";

// A message and information icon tag that shows an overlay modal when you press or hover on the icon
const PopupModalMessageTag = (props: PopupModalMessageTagProps) => {
  const {
    message,
    alternateMessageComp,
    popupSubtext,
    popupTitle,
    popupPosition = "bottom",
    onHover,
  } = props;
  const [showDialog, setShowDialog] = useState(false);
  // Kept true through the fade-out transition so the popup stays mounted until it finishes animating away
  const [rendered, setRendered] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  // Popup is portal-rendered into document.body, so it's not a DOM descendant of containerRef -
  // its own ref is needed so outside-click detection doesn't treat clicks inside it as "outside"
  const popupRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
  };

  // Delay closing so moving the cursor from the icon onto the popup (or vice versa) doesn't flicker it shut
  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(
      () => setShowDialog(false),
      HOVER_CLOSE_DELAY_MS
    );
  };

  useEffect(() => clearCloseTimeout, []);

  useEffect(() => {
    if (showDialog) {
      setRendered(true);
    }
  }, [showDialog]);

  // Position via a portal with fixed coords (from the anchor's rect) so ancestors with overflow:hidden can't clip it
  useLayoutEffect(() => {
    if (!rendered || !containerRef.current) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const left = Math.min(
      Math.max(rect.left, POPUP_MARGIN),
      window.innerWidth - POPUP_WIDTH - POPUP_MARGIN
    );
    setPopupStyle(
      popupPosition === "bottom"
        ? { top: rect.bottom + 6, left }
        : { top: rect.top - 6, left, transform: "translateY(-100%)" }
    );
  }, [rendered, popupPosition]);

  useEffect(() => {
    const handler = (event: any) => {
      if (
        showDialog &&
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !popupRef.current?.contains(event.target)
      ) {
        setShowDialog(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showDialog]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div
        className="AlignedRowSelect"
        style={{ color: Colors.GRAY1, gap: 4 }}
        onMouseEnter={
          onHover
            ? () => {
                clearCloseTimeout();
                setShowDialog(true);
              }
            : undefined
        }
        onMouseLeave={onHover ? scheduleClose : undefined}
        onClick={!onHover ? () => setShowDialog(!showDialog) : undefined}
      >
        {alternateMessageComp || (
          <>
            <span style={{ fontSize: 14 }}>{message}</span>
            <Icon
              icon="mdi:information-outline"
              height={14}
              style={{ cursor: "pointer", color: Colors.BLACK, paddingTop: 2 }}
            />
          </>
        )}
      </div>
      {rendered
        ? createPortal(
            <div
              ref={popupRef}
              className="ColumnNormal"
              onMouseEnter={onHover ? clearCloseTimeout : undefined}
              onMouseLeave={onHover ? scheduleClose : undefined}
              onTransitionEnd={() => {
                if (!showDialog) {
                  setRendered(false);
                }
              }}
              style={{
                boxShadow: "-1px 1px 4px 0px #1E1E371A",
                backgroundColor: Colors.WHITE,
                zIndex: 1000,
                gap: 5,
                width: POPUP_WIDTH,
                padding: 14,
                borderRadius: 8,
                position: "fixed",
                opacity: showDialog ? 1 : 0,
                pointerEvents: showDialog ? "auto" : "none",
                transition: FADE_OUT_TRANSITION,
                ...popupStyle,
              }}
            >
              {popupTitle ? (
                <span style={{ fontSize: 14 }}>{popupTitle}</span>
              ) : null}
              {typeof popupSubtext === "string" ? (
                <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                  {popupSubtext}
                </span>
              ) : (
                popupSubtext
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default PopupModalMessageTag;
